import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';

import { jwtDecode } from 'jwt-decode';
import isEmpty from 'lodash-es/isEmpty';
import loMap from 'lodash-es/map';
import pickBy from 'lodash-es/pickBy';
import { firstValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { EFBUserRole, IFBApiOauthParams, PKCETokenResponse } from '@interfaces';
import browserHelper from '@utils/browser-helper';
import IframeHelper, { IFBIframeMessageEvent } from '@utils/iframe-helper';
import locationHelper from '@utils/location-helper';
import { storage } from '@utils/storage.service';

import { IFBWindow } from '@app/models/window-namespace';
import { CordovaPluginsService } from '@app/shared/modules/cordova/services/cordova-plugins.service.abstract';
import { CompanyService } from '@app/shared/services/company.service';
import { EVENTS } from '@constants';
import { events } from '@utils/events';
import { logger } from '@utils/logger.service';
import { SwitchTsModeService } from './switch-ts-mode.service';
import { IS_MFE_MODE } from '../tokens/is-mfe-mode';

export interface IFBParsedAuthToken {
  company_id: string;
  user_id: string;
  exp: number;
  jti: string;
  client_id: string;
  scope: string[];
  authorities: EFBUserRole[];
  user_name: string;
  sso: boolean;
  aud: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly switchTsModeService = inject(SwitchTsModeService);
  private readonly isMfe = inject(IS_MFE_MODE);
  private IFRAME_TIMEOUT = 15000;
  private MIN_REFRESH_TIMEOUT = 15000;
  private SSO_LOGIN_KEY = 'sso-login';
  private tokenRenewalTimeoutHandle: any;

  constructor(
    @Inject('Window') private window: IFBWindow,
    @Inject('APP') public APP: string,
    private companyService: CompanyService,
    private cordovaPluginsService: CordovaPluginsService,
    private http: HttpClient
  ) {}

  get RENEW_LINK() {
    return `${this.LOGIN_API}?prompt=none&state=silent_auth`;
  }

  /**
   * Get the link to the auth-service login page (HTML).
   */
  get LOGIN_LINK(): Observable<string> {
    const subdomain = this.companyService.subdomain;
    const baseUrl = `https://${subdomain}${subdomain ? '.' : ''}${environment.AUTH_BASE}`;

    return of(`${baseUrl}/site/login`);
  }

  /**
   * Get the URL for the auth-service REST API.
   */
  get LOGIN_API() {
    const subdomain = this.companyService.subdomain;
    return `https://${subdomain}${subdomain ? '.' : ''}${environment.AUTH_BASE}/login`;
  }

  LOGOUT_LINK(): Observable<string> {
    const subdomain = this.companyService.subdomain;
    return this.LOGIN_LINK.pipe(
      map((loginLink: string) => {
        const loginRedirect = `${loginLink}?logout=true`;
        return `https://${subdomain}.${environment.AUTH_BASE}/logout?redirect_uri=${encodeURIComponent(loginRedirect)}`;
      })
    );
  }

  get loggedInViaSso() {
    return storage.getItem(this.SSO_LOGIN_KEY) === 1;
  }

  get href() {
    return this.window.location.pathname + this.window.location.search;
  }

  checkToken(token: string) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post(`https://${environment.AUTH_BASE}/oauth/tokeninfo`, { token }, { headers });
  }

  obtainToken(username: string, password: string, otpToken?: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    const params: any = {
      client_id: '00000000-0000-0000-0000-000000000000',
      client_secret: 'youshallnotpass',
      grant_type: 'password',
      password,
      scope: 'read write',
      username: this.companyService.data.id + ':' + username
    };

    if (otpToken) {
      params.otp_token = otpToken;
    }

    return this.http.post(`https://${environment.API_BASE}/oauth/token`, null, { headers, params });
  }

  pkceObtainToken(code: string, codeVerifier: string, redirectUri: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    const params = {
      client_id: '00000000-0000-0000-0000-000000000006',
      code,
      code_verifier: codeVerifier,
      grant_type: 'authorization_code',
      redirect_uri: redirectUri
    };

    return this.http.post<PKCETokenResponse>(`https://${environment.AUTH_BASE}/oauth/token`, null, { headers, params });
  }

  pkceRefreshAuthToken(refreshToken: string) {
    if (!refreshToken) {
      return throwError(() => 'No refreshToken provided');
    }

    const now = Math.floor(new Date().getTime() / 1000);
    const expiration = jwtDecode<IFBParsedAuthToken>(refreshToken).exp;
    const isRefreshTokenExpired = now >= expiration;
    if (isRefreshTokenExpired) {
      return throwError(() => 'refreshToken has expired');
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    const params = {
      client_id: '00000000-0000-0000-0000-000000000006',
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    };

    return this.http.post<IFBApiOauthParams>(`https://${environment.AUTH_BASE}/oauth/token`, null, { headers, params });
  }

  goldenCookieTokenRequest(): Observable<IFBApiOauthParams | null> {
    return this.exchangeGoldenCookieToken().pipe(
      catchError(error => {
        logger.error('Golden cookie token request failed', error);
        return of(null);
      })
    );
  }

  exchangeGoldenCookieToken(): Observable<IFBApiOauthParams | null> {
    const oauthTokenUrl = '/referrals/oauth/token';
    const headers = new HttpHeaders({
      'golden-cookie': 'true'
    });

    return this.http
      .post<IFBApiOauthParams>(
        oauthTokenUrl,
        {},
        {
          headers,
          withCredentials: true
        }
      )
      .pipe(
        map(response => {
          const token = typeof response?.access_token === 'string' ? response.access_token : null;
          const companyId = response?.company_id || this.getCompanyIdFromToken(token);

          if (token) {
            storage.setItem('token', token);
            this.setLoggedInViaSso(token);
          }

          if (typeof response?.expires_in === 'number') {
            storage.setItem('tokenTTL', response.expires_in);
          }

          if (companyId) {
            this.companyService.set({ id: companyId });
          }

          return token ? { ...response, company_id: companyId || response?.company_id } : null;
        })
      );
  }

  private getCompanyIdFromToken(token?: string | null): string | null {
    if (!token) {
      return null;
    }

    try {
      return jwtDecode<IFBParsedAuthToken>(token).company_id || null;
    } catch {
      return null;
    }
  }

  /**
   * If received parameters after redirect from SSO, update values
   * @param href {string} optional, when using with silent authentication, pass resulting iframe url to parse
   */
  pickupParamsFromLocationHash(href?: string) {
    const silent = !isEmpty(href);

    const accessToken = locationHelper.hashParam('access_token', href);
    const error = locationHelper.hashParam('error', href);
    const errorDescription = locationHelper.hashParam('error_description', href);
    const expiresIn = locationHelper.hashParam('expires_in', href);
    const state = locationHelper.hashParam('state', href);
    const tsMode = locationHelper.hashParam(this.switchTsModeService.tsModeKey, href);
    const mfe = locationHelper.hashParam(this.switchTsModeService.mfeUrlKey, href);

    if (error === 'mfa_mandatory') {
      browserHelper.redirectTo(`/enable-mfa?t=${accessToken}`);
    }

    if (accessToken) {
      storage.setItem('token', accessToken);
    }

    if (expiresIn) {
      storage.setItem('tokenTTL', expiresIn);
    }

    if (tsMode) {
      storage.setItem(this.switchTsModeService.tsModeKey, 'true');
    }

    if (mfe) {
      storage.setItem(this.switchTsModeService.mfeUrlKey, mfe);
    }

    // Check if user is logged in via SSO
    const currentToken = accessToken || storage.getItem('token');
    this.setLoggedInViaSso(currentToken);

    if (!silent) {
      this.window.location.hash = '';
    }

    // For the silent auth returns
    return {
      accessToken,
      error,
      errorDescription,
      expiresIn,
      state
    };
  }

  retryTimesHandler(state: string) {
    const retryTimes = locationHelper.getParam('retry_times', state);

    if (retryTimes) {
      storage.setItem('retry_times', retryTimes);
    } else {
      storage.removeItem('retry_times');
    }
  }

  redirectToApp(token?: string, state?: string) {
    if (token) {
      storage.setItem('token', token);
    }

    if (browserHelper.isInWrapper) {
      browserHelper.redirectTo('/');
      return;
    }

    let redirectTo = state || '/dashboard/';

    if (token) {
      redirectTo += '#access_token=' + token;
    }

    browserHelper.redirectTo(redirectTo);
  }

  /**
   * Calls redirectToLoginWithParams without the prompt=none argument.
   * This way user will always end on a login page.
   *
   * @param state
   * @param error
   * @param errorDescription
   */
  endSession(state?: string, error?: string, errorDescription?: string) {
    this.stopTokenRenewal();
    events.broadcast(EVENTS.AUTH_SERVICE.END_SESSION);
    this.redirectToLoginWithParams({
      error,
      error_description: errorDescription,
      state
    });
  }

  /**
   * Calls redirectToLoginWithParams with the prompt=none argument and current app state.
   * This way before showing user a login page it will be made an attempt to restore backend session.
   */
  restoreSession(state = this.href) {
    this.redirectToLoginWithParams({
      prompt: 'none',
      state
    });
  }

  restoreSessionOnError() {
    if (this.APP !== 'dashboard') {
      return;
    }

    const errorState = this.isMfe ? this.getStateErrorMfe() : this.getStateError();
    const { urlParams, retryTimes } = errorState;
    let { state } = errorState;

    if (isNaN(retryTimes) || retryTimes < 1) {
      state = `${state}${state.includes('?') ? '&' : '?'}retry_times=1`;
    } else {
      urlParams.set('retry_times', (retryTimes + 1).toString());
      state = `${state.split('?')[0]}?${urlParams.toString()}`;
    }

    if (retryTimes > 3) {
      return;
    }

    this.redirectToLoginWithParams({
      prompt: 'none',
      state
    });
  }

  getStateError() {
    const { redirectPath } = this.window.Firstbird;
    let state = redirectPath && this.href === '/dashboard/' ? redirectPath : this.href;
    state = state.replace(/\/$/, '');
    const urlParams = new URLSearchParams(state.split('?')[1]);
    const retryTimes = parseInt(storage.getItem('retry_times') || '0', 10);
    return {
      state,
      urlParams,
      retryTimes
    };
  }

  getStateErrorMfe() {
    const state = this.href;
    const urlParams = new URLSearchParams(state.split('?')[1]);
    const retryTimes = parseInt(storage.getItem('retry_times') || '0', 10);
    return {
      state,
      urlParams,
      retryTimes
    };
  }

  /**
   * Directs user to login page with the params specified.
   * On mobile or dev environment params are ignored.
   * @param params {Object} { state, error, error_description }
   */
  redirectToLoginWithParams(params: any) {
    if (this.isMfe) {
      browserHelper.redirectTo(this.window.location.origin + params.state);
      return;
    }

    const notEmptyParams = pickBy(params, (value: any) => !!value);

    // Build a string with GET parameters
    let getParams = loMap(notEmptyParams, (value, key) => `${key}=${encodeURIComponent(value as string)}`).join('&');

    if (getParams) {
      getParams = '?' + getParams;
    }

    if (browserHelper.isInWrapper) {
      browserHelper.redirectTo('/login' + getParams);
    } else {
      // Web app, user logged in via backend login page
      browserHelper.redirectTo(this.LOGIN_API + getParams);
    }
  }

  setUpTokenRenewal() {
    // In case there was already a timeout set up – stop it
    this.stopTokenRenewal();

    const tokenTTL = parseInt(storage.getItem('tokenTTL'), 10) * 1000;
    // give it a time to retry once if renewal failed
    const refreshTime = Math.max(tokenTTL - this.IFRAME_TIMEOUT * 2, this.MIN_REFRESH_TIMEOUT);

    this.tokenRenewalTimeoutHandle = setTimeout(() => this.renewToken(() => this.setUpTokenRenewal()), refreshTime);
  }

  renewToken(callback: () => any) {
    if (browserHelper.isInWrapper) {
      return this.renewTokenMobile(callback);
    } else {
      return this.renewTokenWeb(callback);
    }
  }

  /**
   * Opens an iframe to query a new token from SSO
   */
  renewTokenWeb(callback: () => any) {
    const domain = this.companyService.domain(environment.API_BASE);
    const subdomain = this.companyService.subdomain;
    const redirectUri = `https://${subdomain}.${domain}/login-oauth-finish.html`;

    const iframe = new IframeHelper({
      expectedState: 'silent_auth',

      onMessage: (event: IFBIframeMessageEvent) => {
        logger.info('renewTokenWeb received a message from iframe', JSON.stringify(event));

        const iframeUrl = event.data.hash as string;
        const hashParams = this.pickupParamsFromLocationHash(iframeUrl);

        if (hashParams.error === 'login_required') {
          this.checkToken(storage.getItem('token'))
            .pipe(first())
            .subscribe({
              next: () => {
                // Token was marked expired wrong, do nothing to prevent getting into a loop.
                // See HUM-8820
              },
              error: err => {
                storage.removeItem('token');
                storage.removeItem('tokenTTL');
                this.endSession(this.href, hashParams.error, hashParams.errorDescription);
                logger.error(err);
                return of(null);
              }
            });
        } else {
          if (callback) {
            callback();
          }
        }
      },
      timeout: this.IFRAME_TIMEOUT,
      timeoutCallback: () => this.renewTokenWeb(callback),
      url: `${this.RENEW_LINK}&redirect_uri=${encodeURIComponent(redirectUri)}`
    });

    // open an iframe and request renewed token
    iframe.init();
  }

  /**
   * When the access_token expires/expired get a new one with the refresh_token
   * @param callback {Function} {Optional}
   * @return {Promise}
   */
  renewTokenMobile(callback?: () => any) {
    const defer$ = new Subject<void>();

    if (!storage.getItem('isInForeground')) {
      defer$.error(null);
      defer$.complete();
      return defer$;
    }

    this.cordovaPluginsService
      .readFromSecureStorage('refresh_token')
      .then((refreshToken: string) => firstValueFrom(this.pkceRefreshAuthToken(refreshToken)))
      // If could not read token from secure storage, try to read it from storage helper
      .catch(() => firstValueFrom(this.pkceRefreshAuthToken(storage.getItem('refreshToken'))))
      .then(response => {
        const { access_token, expires_in } = response;
        storage.setItem('token', access_token);
        storage.setItem('tokenTTL', expires_in);

        if (callback) {
          callback();
        }
        defer$.next();
        defer$.complete();
      })
      .catch(err => {
        if (err && err.status === -1) {
          return firstValueFrom(this.renewTokenMobile(callback)).finally(() => {
            defer$.next();
            defer$.complete();
          });
        }

        this.cordovaPluginsService.deleteFromSecureStorage('refresh_token').finally(() => {
          storage.removeItem('token');
          storage.removeItem('tokenTTL');
          storage.removeItem('refreshToken');

          const hash = this.window.location.hash;
          // There can be no hash when app bootstraps
          const atEnterDomainPage = hash.startsWith('#/enter-domain');
          const atIntroPage = hash.startsWith('#/intro');
          const atLoginPage = hash.startsWith('#/login');
          const atQrCodeScannerPage = hash.startsWith('#/qr-code-scanner');
          const atRegisterPage = hash.startsWith('#/register');
          const atScanQrCodePage = hash.startsWith('#/scan-qr-code');

          const atPublicMobileAppPage =
            atEnterDomainPage ||
            atIntroPage ||
            atLoginPage ||
            atQrCodeScannerPage ||
            atRegisterPage ||
            atScanQrCodePage;

          if (hash && !atPublicMobileAppPage) {
            this.endSession();
          }

          defer$.error(null);
          defer$.complete();
        });
      });

    return defer$;
  }

  stopTokenRenewal() {
    if (this.tokenRenewalTimeoutHandle) {
      clearTimeout(this.tokenRenewalTimeoutHandle);
    }
  }

  setLoggedInViaSso(token?: string) {
    const isJwtLike = typeof token === 'string' && token.split('.').length === 3;
    const isTokenSso = isJwtLike ? this.isTokenSso(token) : false;
    storage.setItem(this.SSO_LOGIN_KEY, isTokenSso ? 1 : 0);
  }

  // JWT handler
  isTokenSso(token: string) {
    const userObject = jwtDecode<IFBParsedAuthToken>(token);

    return !isEmpty(userObject) && userObject.sso;
  }
}
